import React, {
  Ref,
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import "./HosDriverLogGridView.css";
import {
  ButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  makeStyles,
  Menu,
  MenuItem,
  Button as MuiButton,
} from "@material-ui/core";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import PostAddIcon from "@material-ui/icons/PostAdd";
import PlaylistAddIcon from "@material-ui/icons/PlaylistAdd";
import EventNoteIcon from "@material-ui/icons/EventNote";
import {
  Button,
  Confirm,
  Identifier,
  ReactAdminComponentProps,
  useNotify,
  useRefresh,
  useTranslate,
  Responsive,
} from "react-admin";
import { useSelector } from "react-redux";
import { AppState, ThemeName } from "../../../../common/types";
import classnames from "classnames";
import {
  DriverFmcsaInspectionDto,
  DriverLogDetailedView,
  EventApiFactory,
  EventViewDto,
} from "../../../../common/data/service-proxies";
import resourceNames from "../../../../common/data/resourceNames";
import { HosDriverLogGridEvent } from "./HosDriverLogGridEvent";
import { EventHoverContext } from "./HoverContext";
import { useActionPermissions } from "../../../../common/auth/useActionPermissions";
import permissionNames from "../../../../common/auth/access-control/permissionNames";
import { useAxiosInstance } from "../../../../common/hooks/useAxiosInstance";
import { Delete, ViewColumn, MoreHoriz } from "@material-ui/icons";
import { handleAxiosError } from "../../../../common/data/axiosErrorHandler";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import { useAppData } from "../../../../common/hooks/useAppData";
import { ChangeEventsTrailer } from "./bulk-actions/trailers/ChangeEventsTrailer";
import { ChangeEventsShippingDocs } from "./bulk-actions/shipping-docs/ChangeEventsShippingDocs";
import { ChangeEventsCoDriver } from "./bulk-actions/co-driver/ChangeEventsCoDriver";
import { useHistory } from "react-router-dom";
import { ChangeEventsVehicle } from "./bulk-actions/vehicle/ChangeEventsVehicle";
import { IDriverLogGridView } from "./IDriverLogGridView";

type GridFilterType = "basic" | "detailed";

type GridColumnsSetType = "set1" | "set2";

const sets: Record<GridColumnsSetType, string[]> = {
  set1: [
    "status",
    "start",
    "duration",
    "location",
    "vehicle",
    "odometer",
    "engine-hours",
    "origin",
    "notes",
  ],
  set2: [
    "status",
    "start",
    "duration",
    "location",
    "vehicle",
    "co-driver",
    "trailers",
    "shipping-docs",
    "notes",
  ],
};

interface Props extends ReactAdminComponentProps {
  record: DriverLogDetailedView;
  onHover: (id?: Identifier) => void;
}

interface GridFilterProps {
  gridFilter: GridFilterType;
  themeMode: ThemeName;
  setGridFilter: (value: GridFilterType) => void;
}

interface GridColumnsSetFilterProps {
  gridColumnsSetFilter: GridColumnsSetType;
  themeMode: ThemeName;
  setGridColumnsSetFilter: (value: GridColumnsSetType) => void;
}

interface GridButtonsProps {
  selectedRows: number[];
  record: DriverLogDetailedView;
}

const useStyles = makeStyles((x) => ({
  delete: {
    color: x.palette.error.main,
  },
}));

const getColumnsSetButtonClassNames = (
  gridSetType: GridColumnsSetType,
  themeMode: ThemeName
) => {
  let set1ButtonClassNames: string;
  let set2ButtonClassNames: string;
  if (gridSetType === "set1") {
    set1ButtonClassNames = classnames(themeMode);
    set2ButtonClassNames = classnames(themeMode, "inactive-button");
  } else {
    set1ButtonClassNames = classnames(themeMode, "inactive-button");
    set2ButtonClassNames = classnames(themeMode);
  }

  return { set1ButtonClassNames, set2ButtonClassNames };
};

const getButtonClassNames = (
  gridFilter: GridFilterType,
  themeMode: ThemeName
) => {
  let basicButtonClassNames: string;
  let detailedButtonClassNames: string;
  if (gridFilter === "basic") {
    basicButtonClassNames = classnames(themeMode);
    detailedButtonClassNames = classnames(themeMode, "inactive-button");
  } else {
    basicButtonClassNames = classnames(themeMode, "inactive-button");
    detailedButtonClassNames = classnames(themeMode);
  }

  return { basicButtonClassNames, detailedButtonClassNames };
};

const shouldHover = (currentEvent: Identifier, hoveredEventId?: Identifier) =>
  currentEvent === hoveredEventId;

const GridButtons = memo(({ selectedRows, record }: GridButtonsProps) => {
  const classes = useStyles();
  const history = useHistory();
  const [deleting, setDeleting] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const axios = useAxiosInstance();
  const refresh = useRefresh();
  const permissions = useActionPermissions();
  const notify = useNotify();

  const handleBulkDeleteClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      if (!axios) {
        return;
      }

      setDeleting(true);
      EventApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppEventDeleteManyDelete({ ids: selectedRows })
        .then(() => {
          refresh();
        })
        .catch((e) => {
          notify(handleAxiosError(e), { type: "error" });
        })
        .finally(() => {
          setDeleting(false);
          setConfirmOpen(false);
        });
    },
    [axios, selectedRows, notify, refresh]
  );

  const handleAddRemarkClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      history?.push(
        `/${resourceNames.HOS_DRIVER_LOG_VIEW}/${record.driverId}/${record.date}/addRemark`,
        {
          record: {
            driverId: record.driverId,
            date: record.date,
            time: new Date(),
            __endpointOverride: "api/app/event/addRemark",
          },
        }
      );
    },
    [history, record.date, record.driverId]
  );

  const handleAddStatusClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      history?.push(
        `/${resourceNames.HOS_DRIVER_LOG_VIEW}/${record.driverId}/${record.date}/addStatus`,
        {
          record: {
            driverId: record.driverId!,
            date: record.date,
            time: new Date(),
            __endpointOverride: "api/app/event/addStatus",
          },
        }
      );
    },
    [history, record.date, record.driverId]
  );

  const canDelete = permissions && permissions[permissionNames.hos.logs.delete];

  const [
    masterMenuAnchorEl,
    setMasterMenuAnchorEl,
  ] = React.useState<null | HTMLElement>(null);
  const masterMenuOpen = Boolean(masterMenuAnchorEl);

  const handleMasterMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setMasterMenuAnchorEl(event.currentTarget);
  };
  const handleMasterMenuClose = () => {
    setMasterMenuAnchorEl(null);
  };

  return (
    <>
      <ButtonGroup variant="text" color="primary">
        {permissions &&
        (permissions[permissionNames.hos.logs.reassign] ||
          permissions[permissionNames.hos.logs.delete]) ? null : permissions &&
          permissions[permissionNames.hos.logs.update] ? (
          <Button
            label="resources.hos-driver-log-view.labels.addRemark"
            onClick={handleAddRemarkClick}
          >
            <PlaylistAddIcon />
          </Button>
        ) : null}
        {canDelete && !!selectedRows.length ? (
          <Responsive
            medium={
              <>
                <ChangeEventsVehicle
                  latestEvent={
                    record.hosDetails ? record.events?.at(-1)?.id : undefined
                  }
                  selectedRows={selectedRows}
                  currentVehicles={record.events
                    ?.filter((x) => selectedRows.includes(x.id!))
                    .map((x) => x.vehicleId)}
                />
                <ChangeEventsCoDriver
                  selectedRows={selectedRows}
                  currentCoDrivers={record.events
                    ?.filter((x) => selectedRows.includes(x.id!))
                    .map((x) => x.coDriverId)}
                />
                <ChangeEventsShippingDocs
                  latestEvent={
                    record.hosDetails ? record.events?.at(-1)?.id : undefined
                  }
                  selectedRows={selectedRows}
                  currentShippingDocs={record.events
                    ?.filter((x) => selectedRows.includes(x.id!))
                    .map((x) => x.shippingDocuments)}
                />
                <ChangeEventsTrailer
                  latestEvent={
                    record.hosDetails ? record.events?.at(-1)?.id : undefined
                  }
                  selectedRows={selectedRows}
                  currentTrailers={record.events
                    ?.filter((x) => selectedRows.includes(x.id!))
                    .map((x) => x.attachedTrailers)}
                />
                <Button
                  className={`master-action ${classes.delete}`}
                  label="Delete selected"
                  onClick={() => setConfirmOpen(true)}
                >
                  <Delete />
                </Button>
              </>
            }
            small={
              <>
                <Button
                  label="Actions"
                  onClick={handleMasterMenuClick}
                  aria-controls={masterMenuOpen ? "basic-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={masterMenuOpen ? "true" : undefined}
                >
                  <MoreHoriz />
                </Button>
                <Menu
                  open={masterMenuOpen}
                  anchorEl={masterMenuAnchorEl}
                  onClose={handleMasterMenuClose}
                  MenuListProps={{
                    "aria-labelledby": "basic-button",
                  }}
                >
                  <MenuItem>
                    <ChangeEventsVehicle
                      latestEvent={
                        record.hosDetails
                          ? record.events?.at(-1)?.id
                          : undefined
                      }
                      shouldRenderReactAdminButton={false}
                      selectedRows={selectedRows}
                      currentVehicles={record.events
                        ?.filter((x) => selectedRows.includes(x.id!))
                        .map((x) => x.vehicleId)}
                    />
                  </MenuItem>
                  <MenuItem>
                    <ChangeEventsCoDriver
                      selectedRows={selectedRows}
                      shouldRenderReactAdminButton={false}
                      currentCoDrivers={record.events
                        ?.filter((x) => selectedRows.includes(x.id!))
                        .map((x) => x.coDriverId)}
                    />
                  </MenuItem>
                  <MenuItem>
                    <ChangeEventsShippingDocs
                      latestEvent={
                        record.hosDetails
                          ? record.events?.at(-1)?.id
                          : undefined
                      }
                      selectedRows={selectedRows}
                      shouldRenderReactAdminButton={false}
                      currentShippingDocs={record.events
                        ?.filter((x) => selectedRows.includes(x.id!))
                        .map((x) => x.shippingDocuments)}
                    />
                  </MenuItem>
                  <MenuItem>
                    <ChangeEventsTrailer
                      latestEvent={
                        record.hosDetails
                          ? record.events?.at(-1)?.id
                          : undefined
                      }
                      shouldRenderReactAdminButton={false}
                      selectedRows={selectedRows}
                      currentTrailers={record.events
                        ?.filter((x) => selectedRows.includes(x.id!))
                        .map((x) => x.attachedTrailers)}
                    />
                  </MenuItem>
                  {canDelete && !!selectedRows.length ? (
                    <MenuItem>
                      <MuiButton
                        color="primary"
                        variant="text"
                        className={`${classes.delete}`}
                        onClick={() => {
                          setConfirmOpen(true);
                          handleMasterMenuClose();
                        }}
                        startIcon={<Delete />}
                      >
                        Delete selected
                      </MuiButton>
                    </MenuItem>
                  ) : null}
                </Menu>
              </>
            }
          />
        ) : null}
        {permissions && permissions[permissionNames.hos.logs.update] && (
          <Button
            label="resources.hos-driver-log-view.labels.addStatus"
            onClick={handleAddStatusClick}
          >
            <PostAddIcon />
          </Button>
        )}
      </ButtonGroup>
      <Confirm
        loading={deleting}
        isOpen={confirmOpen}
        title="resources.hos-driver-log-view.labels.confirmBulkDeleteTitle"
        content="resources.hos-driver-log-view.labels.confirmBulkDeleteMessage"
        onConfirm={handleBulkDeleteClick}
        onClose={() => {
          if (!deleting) {
            setConfirmOpen(false);
          }
        }}
      />
    </>
  );
});

const GridFilter = ({
  gridFilter,
  themeMode,
  setGridFilter,
}: GridFilterProps) => {
  const {
    basicButtonClassNames,
    detailedButtonClassNames,
  } = getButtonClassNames(gridFilter, themeMode);
  return (
    <ButtonGroup disableElevation variant="contained" color="primary">
      <Button
        label="resources.hos-driver-log-view.labels.basic"
        className={basicButtonClassNames}
        onClick={() => setGridFilter("basic")}
      >
        <CalendarTodayIcon />
      </Button>
      <Button
        label="resources.hos-driver-log-view.labels.detailed"
        className={detailedButtonClassNames}
        onClick={() => setGridFilter("detailed")}
      >
        <EventNoteIcon />
      </Button>
    </ButtonGroup>
  );
};

const GridColumnsSetFilter = ({
  gridColumnsSetFilter,
  themeMode,
  setGridColumnsSetFilter,
}: GridColumnsSetFilterProps) => {
  const {
    set1ButtonClassNames,
    set2ButtonClassNames,
  } = getColumnsSetButtonClassNames(gridColumnsSetFilter, themeMode);

  return (
    <ButtonGroup disableElevation variant="contained" color="primary">
      <Button
        label="Set 1"
        className={set1ButtonClassNames}
        onClick={() => setGridColumnsSetFilter("set1")}
      >
        <ViewColumn />
      </Button>
      <Button
        label="Set 2"
        className={set2ButtonClassNames}
        onClick={() => setGridColumnsSetFilter("set2")}
      >
        <ViewColumn />
      </Button>
    </ButtonGroup>
  );
};

export const HosDriverLogGridView = forwardRef(
  ({ record, onHover, history }: Props, ref: Ref<IDriverLogGridView>) => {
    const [gridFilter, setGridFilter] = useState<GridFilterType>("basic");
    const [gridColumnsSetFilter, setGridColumnsSetFilter] = useState<
      GridColumnsSetType
    >("set1");
    const themeMode = useSelector((x: AppState) => x.theme);
    const translate = useTranslate();
    const { hoveredEventId } = useContext(EventHoverContext);
    const permissions = useActionPermissions();
    const eventTypeCodes = useAppData((x) => x.eventTypeCodes);
    const listRef = useRef<FixedSizeList>(null);

    const handleGraphDutyStatusClick = useCallback(
      (id: Identifier) => {
        if (!hoveredEventId || hoveredEventId !== id) {
          return;
        }

        const data =
          gridFilter === "basic"
            ? record.events?.filter((x) => x.isBasic)
            : record.events;
        if (!data) {
          return;
        }

        const item = data.find((x) => x.id === id);
        if (!item) {
          return;
        }

        const index = data.indexOf(item);
        listRef.current?.scrollToItem(index);
      },
      [gridFilter, hoveredEventId, record.events]
    );

    useImperativeHandle<any, IDriverLogGridView>(ref, () => ({
      selectEvent: handleGraphDutyStatusClick,
    }));

    const isMasterEditSupported = useCallback(
      (record?: EventViewDto) => {
        if (!record) {
          return false;
        }

        const eventType = record.eventType;
        const eventCode = record.eventCode;
        for (const code in eventTypeCodes) {
          if (
            eventTypeCodes[code].eventCode === eventCode &&
            eventTypeCodes[code].eventType === eventType
          ) {
            return true;
          }
        }

        return false;
      },
      [eventTypeCodes]
    );

    useEffect(() => {
      if (
        permissions &&
        (permissions[permissionNames.hos.logs.reassign] ||
          permissions[permissionNames.hos.logs.delete])
      ) {
        setGridFilter("detailed");
      }
    }, [permissions]);

    const setHoveredEventId: React.MouseEventHandler<HTMLTableRowElement> = useCallback(
      (e) => {
        if (onHover) onHover(e.detail);
      },
      [onHover]
    );

    const clearHover: React.MouseEventHandler<HTMLTableRowElement> = useCallback(
      (e) => {
        if (onHover) onHover(undefined);
      },
      [onHover]
    );

    const handleClick: React.MouseEventHandler<HTMLTableRowElement> = useCallback(
      (e) => {
        if (
          permissions &&
          (permissions[permissionNames.hos.logs.reassign] ||
            permissions[permissionNames.hos.logs.rollover]) &&
          isMasterEditSupported(record.events?.find((x) => x.id === e.detail))
        ) {
          history?.push(
            `/${resourceNames.HOS_EVENTS_MASTER}/${
              e.detail
            }?source=${JSON.stringify({
              homeTerminalTimeZone: record.homeTerminalTimeZone,
              driverId: record.driverId,
              date: record.date,
              coDriverId: record.coDrivers?.length
                ? record.coDrivers[0].id
                : null,
            })}`
          );
        } else {
          history?.push(
            `/${resourceNames.HOS_DRIVER_LOG_VIEW}/${record.driverId}/${record.date}/${e.detail}`
          );
        }
      },
      [
        history,
        record.date,
        record.driverId,
        record.homeTerminalTimeZone,
        record.coDrivers,
        permissions,
        isMasterEditSupported,
        record.events,
      ]
    );

    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const handleRowSelected = useCallback(
      (event: EventViewDto, selected: boolean) => {
        if (!event.id) {
          return;
        }

        setSelectedRows((prev) =>
          selected
            ? [...prev, event.id!]
            : [...prev.filter((x) => x !== event.id!)]
        );
      },
      []
    );

    const selectedSet = sets[gridColumnsSetFilter];
    const renderRow = useCallback(
      (
        { data, index, style }: ListChildComponentProps<EventViewDto[] | null>,
        fmcsaInspection?: DriverFmcsaInspectionDto | null
      ) => {
        const event = data?.[index];
        return !event ? null : event.isBasic && event.durationInSeconds ? (
          <HosDriverLogGridEvent
            style={style}
            key={event.id}
            event={event}
            hovered={shouldHover(event.id!, hoveredEventId)}
            onClick={handleClick}
            onMouseEnter={setHoveredEventId}
            onMouseLeave={clearHover}
            homeTerminalTimeZone={record.homeTerminalTimeZone!}
            odd={index % 2 === 1}
            index={index + 1}
            fmcsaInspection={fmcsaInspection}
            gridColumnsSetFilter={sets[gridColumnsSetFilter]}
          />
        ) : gridFilter === "detailed" ? (
          <HosDriverLogGridEvent
            style={style}
            hovered={
              permissions &&
              (permissions[permissionNames.hos.logs.reassign] ||
                permissions[permissionNames.hos.logs.rollover])
                ? shouldHover(event.id!, hoveredEventId)
                : false
            }
            onClick={
              permissions &&
              (permissions[permissionNames.hos.logs.reassign] ||
                permissions[permissionNames.hos.logs.rollover])
                ? handleClick
                : undefined
            }
            onMouseEnter={
              permissions &&
              (permissions[permissionNames.hos.logs.reassign] ||
                permissions[permissionNames.hos.logs.rollover])
                ? setHoveredEventId
                : undefined
            }
            onMouseLeave={
              permissions &&
              (permissions[permissionNames.hos.logs.reassign] ||
                permissions[permissionNames.hos.logs.rollover])
                ? clearHover
                : undefined
            }
            key={event.id}
            event={event}
            homeTerminalTimeZone={record.homeTerminalTimeZone!}
            odd={index % 2 === 1}
            index={index + 1}
            fmcsaInspection={fmcsaInspection}
            gridColumnsSetFilter={sets[gridColumnsSetFilter]}
          />
        ) : null;
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        clearHover,
        gridFilter,
        handleClick,
        handleRowSelected,
        hoveredEventId,
        setHoveredEventId,
        record,
        selectedRows,
        permissions,
        gridColumnsSetFilter,
      ]
    );

    const events =
      gridFilter === "detailed"
        ? record.events ?? []
        : record.events?.filter((x) => x.isBasic && x.durationInSeconds) ?? [];
    const eventsCount = events.length;

    return (
      <div id="hos-driver-log-grid-view-container">
        <section id="hos-driver-log-grid-toolbar">
          <div className="toolbar-left">
            {permissions && permissions[permissionNames.hos.logs.delete] ? (
              <GridColumnsSetFilter
                gridColumnsSetFilter={gridColumnsSetFilter}
                themeMode={themeMode}
                setGridColumnsSetFilter={setGridColumnsSetFilter}
              />
            ) : (
              <GridFilter
                gridFilter={gridFilter}
                themeMode={themeMode}
                setGridFilter={setGridFilter}
              />
            )}
          </div>
          <div className="toolbar-right">
            <GridButtons selectedRows={selectedRows} record={record} />
          </div>
        </section>
        <section id="hos-driver-log-grid-view">
          <TableContainer id="hos-driver-log-grid-table-container">
            <Table component="div" size="small">
              <TableHead component="div">
                <TableRow component="div">
                  <TableCell component="div" className="row-counter">
                    #
                  </TableCell>
                  {selectedSet.includes("status") ? (
                    <TableCell component="div" className="status">
                      {translate("resources.hos-driver-log-view.labels.status")}
                    </TableCell>
                  ) : null}
                  {selectedSet.includes("start") ? (
                    <TableCell component="div" className="start">
                      {translate("resources.hos-driver-log-view.labels.start")}
                    </TableCell>
                  ) : null}
                  {selectedSet.includes("duration") ? (
                    <TableCell component="div" className="duration">
                      {translate(
                        "resources.hos-driver-log-view.labels.duration"
                      )}
                    </TableCell>
                  ) : null}
                  {selectedSet.includes("location") ? (
                    <TableCell component="div" className="location">
                      {translate(
                        "resources.hos-driver-log-view.labels.location"
                      )}
                    </TableCell>
                  ) : null}
                  {selectedSet.includes("vehicle") ? (
                    <TableCell component="div" className="vehicle">
                      Vehicle
                    </TableCell>
                  ) : null}
                  {selectedSet.includes("odometer") ? (
                    <TableCell component="div" className="odometer">
                      {translate(
                        "resources.hos-driver-log-view.labels.odometer"
                      )}
                    </TableCell>
                  ) : null}
                  {selectedSet.includes("co-driver") ? (
                    <TableCell component="div" className="co-driver">
                      Co-driver
                    </TableCell>
                  ) : null}
                  {selectedSet.includes("engine-hours") ? (
                    <TableCell component="div" className="engine-hours">
                      {translate(
                        "resources.hos-driver-log-view.labels.engineHours"
                      )}
                    </TableCell>
                  ) : null}
                  {selectedSet.includes("trailers") ? (
                    <TableCell component="div" className="trailers">
                      Trailer(s)
                    </TableCell>
                  ) : null}
                  {selectedSet.includes("origin") ? (
                    <TableCell component="div" className="origin">
                      {translate("resources.hos-driver-log-view.labels.origin")}
                    </TableCell>
                  ) : null}
                  {selectedSet.includes("shipping-docs") ? (
                    <TableCell component="div" className="shipping-docs">
                      Doc(s)
                    </TableCell>
                  ) : null}
                  {selectedSet.includes("notes") ? (
                    <TableCell component="div" className="notes">
                      {translate("resources.hos-driver-log-view.labels.notes")}
                    </TableCell>
                  ) : null}
                  <TableCell
                    component="div"
                    className={
                      permissions &&
                      permissions[permissionNames.hos.logs.delete]
                        ? "action-buttons-with-delete"
                        : "action-buttons"
                    }
                  ></TableCell>
                </TableRow>
              </TableHead>
              <TableBody component="div">
                <AutoSizer>
                  {(autoSizerProps) => (
                    <FixedSizeList
                      ref={listRef}
                      {...autoSizerProps}
                      itemSize={48}
                      itemCount={eventsCount}
                      itemData={events}
                      itemKey={(i, event) => event![i].id!}
                    >
                      {(props) =>
                        renderRow({ ...props }, record.driverFmcsaInspection)
                      }
                    </FixedSizeList>
                  )}
                </AutoSizer>
              </TableBody>
            </Table>
          </TableContainer>
        </section>
      </div>
    );
  }
);
